import { API_BASE_URL } from "./apiConfig";

type ApiRequestOptions = {
    method?: string;
    body?: unknown;
    token?: string;
    headers?: Record<string, string>;
};

function buildApiUrl(path: string): string {
    return path.startsWith("/") ? `${API_BASE_URL}${path}` : `${API_BASE_URL}/${path}`;
}

function buildHeaders(options: ApiRequestOptions): Record<string, string> {
    const headers: Record<string, string> = { ...options.headers };

    if (options.body !== undefined && !(options.body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
    }

    if (options.token) {
        headers["Authorization"] = `Bearer ${options.token}`;
    }

    return headers;
}

function buildBody(body: unknown): BodyInit | undefined {
    if (body === undefined) return undefined;
    if (body instanceof FormData) return body;
    return JSON.stringify(body);
}

type ApiErrorInfo = {
    message: string;
    code?: string;
};

async function readErrorInfo(response: Response): Promise<ApiErrorInfo> {
    const body = await response.json().catch(() => null);
    if (body?.error?.message && typeof body.error.message === "string") {
        return {
            message: body.error.message,
            code: typeof body.error.code === "string" ? body.error.code : undefined,
        };
    }
    if (body?.message && typeof body.message === "string") {
        return { message: body.message };
    }
    return { message: response.statusText || "Die Anfrage ist fehlgeschlagen." };
}

async function assertOk(response: Response): Promise<void> {
    if (response.ok) return;

    const errorInfo = await readErrorInfo(response);
    const error = new Error(errorInfo.message) as Error & { code?: string; status?: number };
    error.code = errorInfo.code;
    error.status = response.status;
    throw error;
}

async function fetchApi(path: string, options: ApiRequestOptions = {}): Promise<Response> {
    try {
        return await fetch(buildApiUrl(path), {
            method: options.method ?? "GET",
            headers: buildHeaders(options),
            body: buildBody(options.body),
        });
    } catch {
        const error = new Error(
            `Backend nicht erreichbar. Prüfe REACT_APP_API_URL (${API_BASE_URL}).`
        ) as Error & { code?: string };
        error.code = "NETWORK_ERROR";
        throw error;
    }
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const response = await fetchApi(path, options);
    await assertOk(response);

    if (response.status === 204) {
        return undefined as T;
    }

    return await response.json().catch(() => undefined) as T;
}

export async function apiDownload(path: string, filename: string, options: ApiRequestOptions = {}): Promise<void> {
    const response = await fetchApi(path, options);
    await assertOk(response);

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}